<template>
    <div class="ppv2-min-h-screen ppv2-bg-white dark:ppv2-bg-dark">
        <div :class="page === 'ResourcePlanning' && planningSubsection ? 'ppv2-p-0' : 'ppv2-p-6'">
            <ConfigurationHeader
                v-if="page !== 'ResourcePlanning' || !planningSubsection"
                :page="page"
                :title="title"
                :breadcrumb="breadcrumbItems"
                :subtitle="subtitle"
                :description="description"
                :info-message="infoMessage"
                :progress-percentage="progressPercentage"
                @back-action="handleBackAction"
            />

            <component
                :is="currentComponent"
                @card-action="handleCardAction"
                :countries="countries"
                :currencies="currencies"
                :app-setting="appSetting"
                :website-setting="websiteSetting"
                :payment-methods="paymentMethods"
                :gift-enabled="giftEnabled"
                :server-ip="serverIp"
                @section-change="planningSubsection = $event"
                @back-action="handleBackAction"
            />

            <PanelComponent
                v-if="showPanel && selectedCard"
                :title="selectedCard.title"
                :description="selectedCard.description"
                :cancelText="$t('common.cancel')"
                :validateText="$t('common.save')"
                :width="'650px'"
                :content-padding="'0'"
                floating
                @close="closePanel"
                @cancel="closePanel(false)"
                @outside-close="closePanel(false)"
                @validate="validateAndSubmitForm"
                ref="panelComponent"
            >
                <ReservationCancellationRulesForm
                    v-if="selectedCard.id === 5"
                    :card="selectedCard"
                    :countries="countries"
                    :currencies="currencies"
                    :app-setting="appSetting"
                    :establishment="establishment"
                    :website-setting="websiteSetting"
                    @update="updateEstablishment"
                />
            </PanelComponent>
        </div>
    </div>
</template>

<script>
import ConfigurationHeader from '../../Shared/ConfigurationHeader.vue'
import ConfigurationCard from '../../Shared/ConfigurationCard.vue'
import PanelComponent from '../../Shared/PanelComponent.vue'
import establishmentMixin from '../Mixins/establishmentMixin'
import ResourcePlanning from './ResourcePlanning.vue'
import FinancialBilling from './FinancialBilling/FinancialBilling.vue'
import ReservationCancellationRulesForm from './ReservationCancellationRules/ReservationCancellationRulesForm.vue'
import Info from './EstablishmentInfo/Info.vue'
import Default from './Default.vue'
import BaseSwitch from '../../Shared/BaseSwitch.vue'
import { EventBus } from '../../../eventBus.js'

export default {
    name: 'EstablishmentIndex',
    mixins: [establishmentMixin],
    props: {
        websiteSetting: {
            required: true,
        },
        appSetting: {
            required: true,
        },
        establishment: {
            type: Object,
            required: true,
        },
        establishmentCancelRate: {
            type: Array,
            required: true,
        },
        countries: {
            required: true,
        },
        currencies: {
            required: true,
        },
        paymentMethods: {
            type: Array,
            required: true,
        },
        invoiceSequences: {
            type: Array,
            required: true,
        },
        giftEnabled: {
            type: Boolean,
            default: false,
        },
        serverIp: {
            type: String,
            default: '',
        },
    },
    created() {
        this.setDefaultLocal()
        this.$store.commit('establishment/SET_APP_SETTING', this.appSetting)
        this.$store.commit('establishment/SET_ESTABLISHMENT', this.establishment)
        this.$store.commit('establishment/SET_WEBSITE_SETTING', this.websiteSetting)
        this.$store.commit('establishment/SET_PAYMENT_METHODS', this.paymentMethods)
        this.$store.commit('establishment/SET_INVOICE_SEQUENCES', this.invoiceSequences)
        this.$store.commit('establishment/SET_ESTABLISHMENT_CANCEL_RATE', this.establishmentCancelRate)
        window.addEventListener('hashchange', this.checkHash)
    },
    mounted() {
        this.checkHash()
    },
    beforeDestroy() {
        window.removeEventListener('hashchange', this.checkHash)
    },
    components: {
        ConfigurationHeader,
        ConfigurationCard,
        Default,
        Info,
        ResourcePlanning,
        FinancialBilling,
        PanelComponent,
        ReservationCancellationRulesForm,
        BaseSwitch,
    },
    data() {
        return {
            title: this.$t('configuration.title'),
            subtitle: '' /* this.$t('configuration.subtitle'), */,
            description: '' /* this.$t('configuration.description'), */,
            infoMessage: '' /* this.$t('configuration.infoMessage'), */,
            progressPercentage: 45,
            planningSubsection: null,
            selectedCard: null,
            showPanel: false,
            page: null,
            isEnglish: localStorage.getItem('locale') === 'en',
        }
    },

    computed: {
        currentComponent() {
            const allowedPages = ['Info', 'ResourcePlanning', 'FinancialBilling', 'ReservationCancellationRulesForm']
            return allowedPages.includes(this.page) ? this.page : 'Default'
        },
        breadcrumbItems() {
            return [this.$t('configuration.title'), this.$t('configuration.cards.establishment.title')]
        },
    },

    methods: {
        setDefaultLocal() {
            const savedLocale = localStorage.getItem('locale')
            if (savedLocale && ['en', 'fr'].includes(savedLocale)) {
                this.$i18n.locale = savedLocale
            } else {
                this.$i18n.locale = this.establishment.language || 'fr'
                localStorage.setItem('locale', this.$i18n.locale)
            }
        },
        handleCardAction(card) {
            this.selectedCard = { ...card }
            const titleEstablishment = this.$t('configuration.cards.establishment.title')
            const titleSchedule = this.$t('configuration.cards.schedule.title')
            const titleBilling = this.$t('configuration.cards.billing.title')
            const titleReserCancelRules = this.$t('configuration.cards.rules.title')
            const titleBooklet = this.$t('configuration.cards.booklet.title')
            const titleLegal = this.$t('configuration.cards.legal.title')

            /* desactiver titleLegal et titleBooklet provisoirement */
            if (card.title == titleLegal || card.title == titleBooklet) {
                return
            }

            switch (card.title) {
                case titleEstablishment:
                    this.page = 'Info'
                    break
                case titleSchedule:
                    this.page = 'ResourcePlanning'
                    break
                case titleBilling:
                    this.page = 'FinancialBilling'
                    break
                case titleReserCancelRules:
                    this.page = 'Default'
                    this.showPanel = true
                    break
                // default:
                // this.page = 'info'
            }

            this.title = card.title
            this.description = card.description
        },

        handleBackAction(data) {
            this.page = 'Default'
            this.planningSubsection = null

            if (data == null) {
                this.title = this.$t('configuration.title')
                this.subtitle = this.$t('configuration.subtitle')
                this.description = this.$t('configuration.description')
                this.infoMessage = '' /* this.$t('configuration.infoMessage') */
            }
        },

        panelComponents() {
            return {
                5: 'ReservationCancellationRulesForm',
            }
        },
        closePanel() {
            this.showPanel = false
            this.selectedCard = null
        },
        async validateAndSubmitForm() {
            const formComponent =
                this.$refs.panelComponent &&
                this.$refs.panelComponent.$children.find((child) => child.$options.name === 'ReservationCancellationRulesForm')
            if (formComponent && !formComponent.validateForm()) {
                this.$toast.error(this.$t('common.formValidationError'))
                return
            }
            await this.submitForm()
        },
        checkHash() {
            const hash = window.location.hash
            if (hash.startsWith('#info')) {
                this.page = 'Info'
                this.title = this.$t('configuration.cards.establishment.title')
                this.description = this.$t('configuration.cards.establishment.description')

                // Parse sub-hash if present (e.g., #info:general)
                if (hash.includes(':')) {
                    const subHash = hash.split(':')[1]
                    this.$nextTick(() => {
                        EventBus.$emit('open-establishment-section', subHash)
                    })
                }
            } else if (hash.startsWith('#schedule')) {
                this.page = 'ResourcePlanning'
                this.title = this.$t('configuration.cards.schedule.title')
                this.description = this.$t('configuration.cards.schedule.description')
            } else if (hash.startsWith('#billing')) {
                this.page = 'FinancialBilling'
                this.title = this.$t('configuration.cards.billing.title')
                this.description = this.$t('configuration.cards.billing.description')

                // Parse sub-hash if present (e.g., #billing:stripeSetup)
                if (hash.includes(':')) {
                    const subHash = hash.split(':')[1]
                    this.$nextTick(() => {
                        EventBus.$emit('open-establishment-section', subHash)
                    })
                }
            } else if (hash === '#payment') {
                this.page = 'Payment'
                this.title = 'Paiement'
                this.description = 'Historique des paiements'
            }
        },
    },
}
</script>

<style scoped>
.ppv2-p-0-forced,
p {
    margin-bottom: 0 !important;
}

.bgGray {
    background-color: #f3f4f6;
}

.dark .bgGray {
    background-color: #060b28;
}

.dark .ppv2-bg-white {
    background-color: rgba(255, 255, 255, 0.1);
}
</style>
